import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Home, User, Calendar, FileText, Loader2, ChevronDown, ChevronRight,
  CheckCircle, Camera, PenTool, AlertTriangle, Zap, Building2
} from "lucide-react";
import { API } from "@/App";
import SignaturePad from "@/components/SignaturePad";

const CONDITION_COLORS = {
  excellent: "bg-green-500",
  good: "bg-emerald-500",
  fair: "bg-amber-500",
  poor: "bg-orange-500",
  very_poor: "bg-red-500",
  new: "bg-blue-500"
};

export default function LandlordReportViewPage() {
  const { token } = useParams();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRooms, setExpandedRooms] = useState([]);
  const [showSignature, setShowSignature] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [savingSignature, setSavingSignature] = useState(false);
  const [showPhoto, setShowPhoto] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [token]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory-reports/landlord-view/${token}`);
      setReport(response.data);
      setExpandedRooms(response.data.rooms?.map(r => r.room_id) || []);
      // Pre-fill landlord name if available
      if (response.data.landlord_name) {
        setSignerName(response.data.landlord_name);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.response?.data?.detail || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  };

  const handleSignature = async (signatureData) => {
    if (!signerName.trim()) {
      toast.error("Please enter your full name");
      return;
    }

    setSavingSignature(true);
    try {
      await axios.post(
        `${API}/inventory-reports/landlord-sign/${token}?signer_name=${encodeURIComponent(signerName)}&signature_data=${encodeURIComponent(signatureData)}`
      );
      toast.success("Thank you! The report is now complete. All parties will receive a copy.");
      setShowSignature(false);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save signature");
    } finally {
      setSavingSignature(false);
    }
  };

  const toggleRoom = (roomId) => {
    setExpandedRooms(prev => 
      prev.includes(roomId) 
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Link Not Found</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">
              Please contact Goodacres if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tenantSigned = report?.tenant_signed;
  const landlordSigned = report?.landlord_signed;
  const totalItems = report?.rooms?.reduce((sum, r) => sum + (r.items?.length || 0), 0) || 0;
  const totalPhotos = report?.rooms?.reduce((sum, r) => sum + (r.photo_count || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{report.report_type_label}</h1>
              <p className="text-muted-foreground">{report.property_address}</p>
            </div>
            <Badge variant={report.status === 'complete' ? 'default' : 'secondary'} className="capitalize">
              {report.status?.replace('_', ' ')}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Report Info Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <Calendar className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                <p className="text-xs text-muted-foreground">Visit Date</p>
                <p className="font-medium">
                  {report.visit_date ? format(new Date(report.visit_date), "dd MMM yyyy") : "N/A"}
                </p>
              </div>
              <div>
                <Home className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                <p className="text-xs text-muted-foreground">Rooms</p>
                <p className="font-medium">{report.rooms?.length || 0}</p>
              </div>
              <div>
                <FileText className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                <p className="text-xs text-muted-foreground">Items</p>
                <p className="font-medium">{totalItems}</p>
              </div>
              <div>
                <Camera className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                <p className="text-xs text-muted-foreground">Photos</p>
                <p className="font-medium">{totalPhotos}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Landlord Greeting */}
        <Card className="mb-6 bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-purple-600" />
              <div>
                <p className="font-medium">Hello, {report.landlord_name || "Landlord"}</p>
                {tenantSigned && !landlordSigned ? (
                  <p className="text-sm text-muted-foreground">
                    <strong>{report.tenant_name}</strong> has already signed this report. 
                    Please review and add your signature to complete the process.
                  </p>
                ) : landlordSigned ? (
                  <p className="text-sm text-green-600">
                    This report is complete. All parties have been sent a copy.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Waiting for tenant to sign first.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signing Progress */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Signing Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${tenantSigned ? 'bg-green-100' : 'bg-slate-100'}`}>
                {tenantSigned ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
                )}
                <span className={tenantSigned ? 'text-green-700 font-medium' : 'text-slate-500'}>
                  1. Tenant
                </span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200" />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${landlordSigned ? 'bg-green-100' : 'bg-slate-100'}`}>
                {landlordSigned ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
                )}
                <span className={landlordSigned ? 'text-green-700 font-medium' : 'text-slate-500'}>
                  2. Landlord
                </span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200" />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${report.status === 'complete' ? 'bg-green-100' : 'bg-slate-100'}`}>
                {report.status === 'complete' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
                )}
                <span className={report.status === 'complete' ? 'text-green-700 font-medium' : 'text-slate-500'}>
                  3. Complete
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meter Readings */}
        {report.meter_readings?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Meter Readings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {report.meter_readings.map((meter) => (
                  <div key={meter.meter_id} className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground capitalize">{meter.meter_type}</p>
                    <p className="text-2xl font-mono font-bold">{meter.reading}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rooms */}
        <div className="space-y-4 mb-6">
          <h2 className="text-lg font-semibold">Room-by-Room Inventory</h2>
          
          {report.rooms?.map((room) => (
            <Card key={room.room_id} className="overflow-hidden">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => toggleRoom(room.room_id)}
              >
                <div className="flex items-center gap-3">
                  {expandedRooms.includes(room.room_id) ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <h3 className="font-semibold">{room.room_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {room.items?.length || 0} items • {room.photo_count || 0} photos
                    </p>
                  </div>
                </div>
              </div>

              {expandedRooms.includes(room.room_id) && (
                <div className="border-t px-4 pb-4">
                  {room.items?.map((item) => (
                    <div key={item.item_id} className="py-4 border-b last:border-b-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{item.item_name}</h4>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1">
                              <span className={`w-3 h-3 rounded-full ${CONDITION_COLORS[item.condition] || 'bg-slate-400'}`}></span>
                              <span className="text-sm capitalize">{item.condition?.replace('_', ' ')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={`w-3 h-3 rounded-full ${CONDITION_COLORS[item.cleanliness] || 'bg-slate-400'}`}></span>
                              <span className="text-sm capitalize">{item.cleanliness?.replace('_', ' ')}</span>
                            </div>
                          </div>
                          {item.notes && (
                            <p className="text-sm text-muted-foreground mt-2 bg-slate-50 p-2 rounded">
                              {item.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {item.photos?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {item.photos.map((photo) => (
                            <img 
                              key={photo.photo_id}
                              src={photo.photo_data}
                              alt=""
                              className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80"
                              onClick={() => setShowPhoto(photo)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Existing Signatures */}
        {report.signatures?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Signatures</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {report.signatures.map((sig) => (
                  <div key={sig.signature_id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <Badge variant={sig.signer_type === 'tenant' ? 'default' : 'secondary'} className="mb-2 capitalize">
                        {sig.signer_type}
                      </Badge>
                      <p className="font-medium">{sig.signer_name}</p>
                      {sig.signature_data && (
                        <img 
                          src={sig.signature_data} 
                          alt="Signature" 
                          className="max-h-16 mt-2 border rounded bg-white"
                        />
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Signed: {format(new Date(sig.signed_at), "dd MMM yyyy 'at' HH:mm")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sign Button - Only show if tenant has signed and landlord hasn't */}
        {tenantSigned && !landlordSigned && (
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-6 text-center">
              <PenTool className="w-12 h-12 mx-auto text-purple-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Your Signature Required</h3>
              <p className="text-muted-foreground mb-4">
                The tenant has signed. Please review the report and add your signature to complete the process.
                Once signed, all parties will receive a copy of the completed report.
              </p>
              <Button 
                size="lg" 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => setShowSignature(true)}
              >
                <PenTool className="w-5 h-5 mr-2" />
                Sign This Report
              </Button>
            </CardContent>
          </Card>
        )}

        {landlordSigned && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Report Complete!</h3>
              <p className="text-muted-foreground">
                Thank you for signing. A copy of the completed report has been sent to all parties.
              </p>
            </CardContent>
          </Card>
        )}

        {!tenantSigned && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">Waiting for Tenant</h3>
              <p className="text-muted-foreground">
                The tenant needs to sign this report before you can add your signature.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Photo Lightbox */}
      <Dialog open={!!showPhoto} onOpenChange={() => setShowPhoto(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none">
          {showPhoto && (
            <img 
              src={showPhoto.photo_data} 
              alt="" 
              className="max-w-full max-h-[80vh] mx-auto rounded"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Landlord Signature
            </DialogTitle>
            <DialogDescription>
              Please enter your name and sign below to complete the inventory report.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Your Full Name</Label>
              <Input 
                placeholder="Enter your full legal name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg text-sm">
              <p className="font-medium text-purple-800 mb-2">Declaration:</p>
              <p className="text-purple-700">
                I confirm that I have reviewed this inventory report and agree that it accurately 
                represents the condition of the property at the time of inspection. I understand 
                that this signature is legally binding and may be used in any future dispute.
              </p>
            </div>

            {savingSignature ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mr-3" />
                <span>Completing report...</span>
              </div>
            ) : (
              <SignaturePad 
                onSave={handleSignature}
                onClear={() => {}}
                width={500}
                height={200}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignature(false)} disabled={savingSignature}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
